import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { compact, inr } from "@/lib/format";
import { Skeleton, StatCard, SkuThumb } from "@/components/Shared";
import { SyncHealth } from "@/components/SyncHealth";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { RefreshCw, Upload, Send, Download } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";

const iso = (d) => d.toLocaleDateString("en-CA", { timeZone: "Asia/Kolkata" });
const PRESETS = [
  { label: "Today", fn: () => { const t = new Date(); return [iso(t), iso(t)]; } },
  { label: "Yesterday", fn: () => { const t = new Date(Date.now() - 864e5); return [iso(t), iso(t)]; } },
  { label: "Last 7d", fn: () => [iso(new Date(Date.now() - 6 * 864e5)), iso(new Date())] },
  { label: "Last 30d", fn: () => [iso(new Date(Date.now() - 29 * 864e5)), iso(new Date())] },
  { label: "This month", fn: () => { const t = iso(new Date()); return [`${t.slice(0, 8)}01`, t]; } },
];

const TelegramCard = () => {
  const [cfg, setCfg] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/tools/telegram/settings").then(({ data }) => setCfg(data));
  useEffect(() => { load(); }, []);

  if (!cfg) return null;

  const connect = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/tools/telegram/connect");
      toast.success(`Telegram connected — ${data.chat_name}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Connect failed");
    } finally {
      setBusy(false);
    }
  };

  const sendNow = async () => {
    setBusy(true);
    try {
      await api.post("/tools/telegram/send-now");
      toast.success("Dashboard PDF sent to Telegram 📨");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Send failed");
    } finally {
      setBusy(false);
    }
  };

  const toggleDaily = async (v) => {
    await api.post("/tools/telegram/settings", { daily_enabled: v, send_hour: cfg.send_hour || 9 });
    setCfg({ ...cfg, daily_enabled: v });
    toast.success(v ? `Daily PDF at ${cfg.send_hour || 9}:00 IST enabled` : "Daily PDF disabled");
  };

  return (
    <div className="bg-card border border-border rounded-lg p-4 shadow-sm flex items-center justify-between flex-wrap gap-3" data-testid="telegram-card">
      <div className="flex items-center gap-2">
        <Send className="h-4 w-4 text-sky-500" aria-hidden="true" />
        <div>
          <p className="text-sm font-semibold">Telegram dashboard PDF</p>
          <p className="text-xs text-muted-foreground">
            {cfg.connected ? `Connected: ${cfg.chat_name}${cfg.last_sent ? ` · last sent ${new Date(cfg.last_sent).toLocaleString("en-IN", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}` : ""}`
              : "Message your bot on Telegram once, then click Connect"}
          </p>
        </div>
      </div>
      <div className="flex items-center gap-3">
        {cfg.connected ? (
          <>
            <label className="flex items-center gap-2 text-xs text-muted-foreground">
              Daily at {cfg.send_hour || 9}:00 IST
              <Switch checked={cfg.daily_enabled} onCheckedChange={toggleDaily} data-testid="telegram-daily-toggle" />
            </label>
            <Button size="sm" className="rounded-full" onClick={sendNow} disabled={busy} data-testid="telegram-send-button">
              <Send className="h-3.5 w-3.5 mr-1.5" aria-hidden="true" /> {busy ? "Sending…" : "Send PDF now"}
            </Button>
          </>
        ) : (
          <Button size="sm" className="rounded-full" onClick={connect} disabled={busy} data-testid="telegram-connect-button">
            {busy ? "Connecting…" : "Connect Telegram"}
          </Button>
        )}
      </div>
    </div>
  );
};

const CategorySkusDialog = ({ category, range, onClose }) => {
  const [d, setD] = useState(null);
  useEffect(() => {
    setD(null);
    if (category)
      api.get(`/tools/amazon-dashboard/category-skus?category=${encodeURIComponent(category)}&start=${range[0]}&end=${range[1]}`)
        .then(({ data }) => setD(data));
  }, [category, range]);

  return (
    <Dialog open={!!category} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto" data-testid="category-skus-dialog">
        <DialogHeader>
          <DialogTitle>{category} — top selling products</DialogTitle>
          <DialogDescription>
            {range[0]} → {range[1]}{d && ` · ${inr(d.category_revenue)} revenue · ${d.category_units.toLocaleString("en-IN")} units · showing top ${d.skus.length} of ${d.total_skus} SKUs`}
          </DialogDescription>
        </DialogHeader>
        {!d ? <Skeleton className="h-64" /> : d.skus.length === 0 ? (
          <p className="text-sm text-muted-foreground p-4">No sales in this category for the selected range.</p>
        ) : (
          <table className="w-full text-sm" data-testid="category-skus-table">
            <thead className="bg-muted/60 text-xs uppercase tracking-wider text-muted-foreground sticky top-0">
              <tr>
                <th className="text-left px-3 py-2.5 font-semibold" colSpan={2}>#&nbsp;&nbsp;Product</th>
                <th className="text-left px-3 py-2.5 font-semibold">SKU</th>
                <th className="text-right px-3 py-2.5 font-semibold">Units</th>
                <th className="text-right px-3 py-2.5 font-semibold">Revenue</th>
                <th className="text-right px-3 py-2.5 font-semibold">ASP</th>
                <th className="text-right px-3 py-2.5 font-semibold">Share</th>
              </tr>
            </thead>
            <tbody>
              {d.skus.map((s, i) => (
                <tr key={s.sku} className="border-t border-border hover:bg-muted/40 transition-colors" data-testid={`cat-sku-row-${s.sku}`}>
                  <td className="pl-3 py-2 w-14">
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-muted-foreground w-5 text-right">{i + 1}</span>
                      <SkuThumb sku={s.sku} alt={s.title} />
                    </div>
                  </td>
                  <td className="px-3 py-2 font-medium max-w-[18rem] truncate">{s.title || s.sku}</td>
                  <td className="px-3 py-2 font-mono text-xs text-muted-foreground">{s.sku}</td>
                  <td className="px-3 py-2 text-right">{s.units.toLocaleString("en-IN")}</td>
                  <td className="px-3 py-2 text-right font-semibold">{inr(s.revenue)}</td>
                  <td className="px-3 py-2 text-right">{s.asp != null ? inr(s.asp) : "—"}</td>
                  <td className="px-3 py-2 text-right text-muted-foreground">{s.share}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </DialogContent>
    </Dialog>
  );
};

const UnmappedDialog = ({ open, range, onClose, onUpload }) => {
  const [d, setD] = useState(null);
  useEffect(() => {
    setD(null);
    if (open)
      api.get(`/tools/amazon-dashboard/unmapped?start=${range[0]}&end=${range[1]}`)
        .then(({ data }) => setD(data));
  }, [open, range]);

  const download = async () => {
    try {
      const res = await api.get(`/tools/amazon-dashboard/unmapped/export?start=${range[0]}&end=${range[1]}`,
        { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `unmapped-skus-${range[1]}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Download failed");
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto" data-testid="unmapped-dialog">
        <DialogHeader>
          <DialogTitle>Unmapped SKUs</DialogTitle>
          <DialogDescription>
            These SKUs sold in {range[0]} → {range[1]} but have no category, so their sales land in the UNMAPPED bucket.
            {d && ` ${d.count} SKUs · ${inr(d.revenue)} revenue · ${d.units.toLocaleString("en-IN")} units`}
          </DialogDescription>
        </DialogHeader>
        <div className="flex items-center gap-2 flex-wrap">
          <Button size="sm" className="rounded-full" onClick={download} data-testid="unmapped-download">
            <Download className="h-3.5 w-3.5 mr-1.5" aria-hidden="true" /> Download Excel to fill
          </Button>
          <Button size="sm" variant="outline" className="rounded-full" onClick={onUpload} data-testid="unmapped-upload">
            <Upload className="h-3.5 w-3.5 mr-1.5" aria-hidden="true" /> Upload filled sheet
          </Button>
          <span className="text-xs text-muted-foreground">
            Fill the <strong>Category</strong> column (existing categories are on sheet 2) and upload — only those rows get added.
          </span>
        </div>
        {!d ? <Skeleton className="h-64" /> : d.skus.length === 0 ? (
          <p className="text-sm text-muted-foreground p-4">Every SKU that sold in this range is mapped. 🎉</p>
        ) : (
          <table className="w-full text-sm" data-testid="unmapped-table">
            <thead className="bg-muted/60 text-xs uppercase tracking-wider text-muted-foreground sticky top-0">
              <tr>
                <th className="text-left px-3 py-2.5 font-semibold" colSpan={2}>Product</th>
                <th className="text-left px-3 py-2.5 font-semibold">SKU</th>
                <th className="text-right px-3 py-2.5 font-semibold">Units</th>
                <th className="text-right px-3 py-2.5 font-semibold">Revenue</th>
              </tr>
            </thead>
            <tbody>
              {d.skus.map((s) => (
                <tr key={s.sku} className="border-t border-border hover:bg-muted/40 transition-colors" data-testid={`unmapped-row-${s.sku}`}>
                  <td className="pl-3 py-2 w-14"><SkuThumb sku={s.sku} alt={s.title} /></td>
                  <td className="px-3 py-2 font-medium max-w-[20rem] truncate" title={s.title}>{s.title || s.sku}</td>
                  <td className="px-3 py-2 font-mono text-xs text-muted-foreground">{s.sku}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{s.units.toLocaleString("en-IN")}</td>
                  <td className="px-3 py-2 text-right tabular-nums font-semibold">{inr(s.revenue)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </DialogContent>
    </Dialog>
  );
};

export const AmazonDashboard = () => {
  const [range, setRange] = useState(PRESETS[3].fn());
  const [data, setData] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [openCat, setOpenCat] = useState(null);
  const [openUnmapped, setOpenUnmapped] = useState(false);
  const fileRef = useRef();

  const load = () => api.get(`/tools/amazon-dashboard?start=${range[0]}&end=${range[1]}`).then(({ data }) => setData(data));
  useEffect(() => { setData(null); load(); }, [range]);

  const refresh = async () => {
    setSyncing(true);
    await api.post("/tools/amazon-dashboard/sync");
    toast.info("Pulling latest orders from Amazon…");
    let tries = 0;
    const poll = setInterval(async () => {
      tries += 1;
      const { data: d } = await api.get(`/tools/amazon-dashboard?start=${range[0]}&end=${range[1]}`);
      if (!d.quick_sync_running || tries > 20) {
        clearInterval(poll);
        setData(d);
        setSyncing(false);
        if (d.quick_sync_running) toast.info("Sync continuing in background — numbers will keep updating");
        else toast.success("Sales data refreshed");
      }
    }, 12000);
  };

  const uploadMap = async (e) => {
    const f = e.target.files[0];
    if (!f) return;
    const fd = new FormData();
    fd.append("file", f);
    try {
      const { data: d } = await api.post("/tools/amazon-dashboard/categories", fd);
      toast.success(`${d.uploaded} SKU mappings saved (${d.total_mapped} total)`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    }
    e.target.value = "";
  };

  if (!data) return <div className="space-y-4"><Skeleton className="h-24" /><Skeleton className="h-64" /></div>;
  const r = data.range;

  return (
    <div className="space-y-6" data-testid="amazon-dashboard">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3 flex-wrap">
          <SyncHealth />
          <p className="text-xs text-muted-foreground">
            All dates in IST · data as of {data.data_as_of ? new Date(data.data_as_of).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" }) : "last daily sync"}
            {data.quick_sync_running && " · refreshing…"}
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button size="sm" variant="outline" className="rounded-full" onClick={() => fileRef.current.click()} data-testid="dash-upload-categories">
            <Upload className="h-3.5 w-3.5 mr-1.5" aria-hidden="true" /> Update category mapping
          </Button>
          <input ref={fileRef} type="file" accept=".csv,.xlsx" className="hidden" onChange={uploadMap} data-testid="dash-categories-file" />
          <Button size="sm" className="rounded-full" onClick={refresh} disabled={syncing} data-testid="dash-refresh-button">
            <RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${syncing ? "animate-spin" : ""}`} aria-hidden="true" />
            {syncing ? "Refreshing…" : "Refresh from Amazon"}
          </Button>
        </div>
      </div>

      <TelegramCard />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard label={`Today (${data.ist_today})`} value={inr(data.today.item_revenue)} sub={`${data.today.orders} orders · ${data.today.units} units`} testId="dash-today-kpi" />
        <StatCard label="Yesterday" value={inr(data.yesterday.item_revenue)} sub={`${data.yesterday.orders} orders · ${data.yesterday.units} units`} testId="dash-yesterday-kpi" />
        <StatCard label="Selected range sales" value={compact(r.item_revenue)} sub={`${range[0]} → ${range[1]}`} testId="dash-range-kpi" />
        <StatCard label="Range orders / units" value={`${r.orders.toLocaleString("en-IN")}`} sub={`${r.units.toLocaleString("en-IN")} units`} testId="dash-range-orders-kpi" />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="dash-asp-split">
        {(r.asp_split || []).map((b) => (
          <div key={b.label} className="bg-card border border-border rounded-lg p-4 shadow-sm" data-testid={`dash-asp-${b.label.includes("999") ? "low" : "high"}`}>
            <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{b.label}</p>
            <p className="text-2xl font-heading font-bold mt-1">{inr(b.revenue)} <span className="text-sm font-normal text-muted-foreground">({b.share}%)</span></p>
            <p className="text-xs text-muted-foreground mt-1">{b.units.toLocaleString("en-IN")} units · {b.skus} SKUs · ASP {b.units ? inr(Math.round(b.revenue / b.units)) : "—"}</p>
          </div>
        ))}
        {r.crockery && (
          <div className="bg-card border border-border rounded-lg p-4 shadow-sm border-l-4 border-l-amber-500" data-testid="dash-crockery">
            <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Crockery (UPC — mugs, cups & saucers, tea/tray sets)</p>
            <p className="text-2xl font-heading font-bold mt-1">{inr(r.crockery.revenue)} <span className="text-sm font-normal text-muted-foreground">({r.crockery.share}%)</span></p>
            <p className="text-xs text-muted-foreground mt-1">{r.crockery.units.toLocaleString("en-IN")} units · {r.crockery.skus} SKUs matched by title keywords</p>
          </div>
        )}
      </div>

      <div className="bg-card border border-border rounded-lg shadow-sm p-4 flex items-end gap-3 flex-wrap" data-testid="dash-date-filter">
        <label className="text-xs text-muted-foreground space-y-1">
          <span>From</span>
          <input type="date" className="block h-9 rounded-md border border-border bg-background px-3 text-sm" value={range[0]}
            onChange={(e) => setRange([e.target.value, range[1]])} data-testid="dash-start-date" />
        </label>
        <label className="text-xs text-muted-foreground space-y-1">
          <span>To</span>
          <input type="date" className="block h-9 rounded-md border border-border bg-background px-3 text-sm" value={range[1]}
            onChange={(e) => setRange([range[0], e.target.value])} data-testid="dash-end-date" />
        </label>
        <div className="flex gap-1.5 flex-wrap">
          {PRESETS.map((p) => (
            <button key={p.label} onClick={() => setRange(p.fn())} data-testid={`dash-preset-${p.label.toLowerCase().replace(/ /g, "-")}`}
              className="h-8 px-3 rounded-full border border-border text-xs font-medium hover:bg-muted transition-colors">
              {p.label}
            </button>
          ))}
        </div>
      </div>

      {data.daily.length > 1 && (
        <div className="bg-card border border-border rounded-lg p-6 shadow-sm" data-testid="dash-daily-chart">
          <h3 className="font-heading text-base font-semibold tracking-tight mb-4">Daily sales (IST)</h3>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={data.daily}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="hsl(var(--border))" />
              <XAxis dataKey="date" tick={{ fontSize: 10 }} stroke="hsl(var(--muted-foreground))" tickFormatter={(v) => v.slice(5)} />
              <YAxis tick={{ fontSize: 10 }} tickFormatter={(v) => compact(v)} stroke="hsl(var(--muted-foreground))" />
              <Tooltip formatter={(v, n) => n === "revenue" ? [inr(v), "Revenue"] : [v, "Orders"]}
                contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 12 }} />
              <Bar dataKey="revenue" fill="#2563EB" radius={[3, 3, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-card border border-border rounded-lg p-6 shadow-sm" data-testid="dash-category-chart">
          <h3 className="font-heading text-base font-semibold tracking-tight mb-4">Sales by category</h3>
          <ResponsiveContainer width="100%" height={Math.max(280, (r.categories?.length || 0) * 26)}>
            <BarChart data={r.categories} layout="vertical" margin={{ left: 80 }}>
              <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="hsl(var(--border))" />
              <XAxis type="number" tick={{ fontSize: 10 }} tickFormatter={(v) => compact(v)} stroke="hsl(var(--muted-foreground))" />
              <YAxis type="category" dataKey="category" tick={{ fontSize: 10 }} width={140} stroke="hsl(var(--muted-foreground))" />
              <Tooltip formatter={(v) => [inr(v), "Revenue"]} contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 12 }} />
              <Bar dataKey="revenue" fill="#D97706" radius={[0, 3, 3, 0]} cursor="pointer"
                onClick={(bar) => bar?.category && setOpenCat(bar.category)} />
            </BarChart>
          </ResponsiveContainer>
          <p className="text-[11px] text-muted-foreground mt-2">Click a bar or a row to see the top products in that category.</p>
        </div>
        <div className="bg-card border border-border rounded-lg shadow-sm overflow-hidden" data-testid="dash-category-table">
          <div className="px-5 py-4 border-b border-border flex items-center justify-between">
            <h3 className="font-heading text-base font-semibold tracking-tight">Category breakdown</h3>
            {r.unmapped_sku_count > 0 && (
              <button onClick={() => setOpenUnmapped(true)}
                      className="text-xs text-amber-600 font-medium hover:underline"
                      data-testid="dash-unmapped-button">
                {r.unmapped_sku_count} SKUs unmapped — fix
              </button>
            )}
          </div>
          <table className="w-full text-sm">
            <thead className="bg-muted/60 text-xs uppercase tracking-wider text-muted-foreground">
              <tr>{["Category", "Revenue", "Units", "Share"].map((h) => <th key={h} className="text-left px-4 py-3 font-semibold">{h}</th>)}</tr>
            </thead>
            <tbody>
              {(r.categories || []).map((c) => (
                <tr key={c.category} onClick={() => setOpenCat(c.category)}
                  className="border-t border-border hover:bg-muted/40 transition-colors cursor-pointer" data-testid={`dash-cat-${c.category}`}>
                  <td className="px-4 py-2.5 font-medium text-blue-600">{c.category}</td>
                  <td className="px-4 py-2.5 font-semibold">{inr(c.revenue)}</td>
                  <td className="px-4 py-2.5">{c.units.toLocaleString("en-IN")}</td>
                  <td className="px-4 py-2.5">{c.share}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      <p className="text-xs text-muted-foreground">
        Sales figures are item-level (product price × qty, matching the category split) so today's pending orders are counted immediately. Today's numbers refresh hourly automatically; a full sync runs every early morning (2–7 AM IST).
      </p>
      <CategorySkusDialog category={openCat} range={range} onClose={() => setOpenCat(null)} />
      <UnmappedDialog open={openUnmapped} range={range} onClose={() => setOpenUnmapped(false)}
                      onUpload={() => fileRef.current.click()} />
    </div>
  );
};
